import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { FilterProvider, useFilter } from "@/partner/shared/components/Filter";
import {
  FilterContext,
  type FilterContextValue,
} from "@/partner/shared/components/Filter/FilterContext";
import { ChatMessagesFilter } from "./ChatMessagesFilter";
import { ChatMessagesList } from "./ChatMessagesList";
import { ChatBansList } from "./ChatBansList";
import {
  CHAT_MESSAGES_PARAMS,
  type ChatTab,
  parseChatTab,
} from "../model/tabs";

// Сообщения и баны — один пункт меню и одна страница с переключателем
// вкладок (?tab=messages|bans, как в Chargebacks.tsx). Заголовок страницы
// стоит над вкладками, а не в карточке фильтра: фильтр есть только у
// сообщений, иначе вкладка банов осталась бы без названия.
export const Chat: FC = () => {
  return (
    <FilterProvider>
      <ChatContent />
    </FilterProvider>
  );
};

// `FilterProvider.onClearFilter` делает `setSearchParams({})` — вместе с
// фильтром это стёрло бы и `?tab=`. Подменяем только этот обработчик, сам
// провайдер (общий файл) не трогаем.
const ChatFilterScope: FC<{ tab: ChatTab; children: ReactNode }> = ({
  tab,
  children,
}) => {
  const filterContext = useFilter();
  const [, setSearchParams] = useSearchParams();

  const value: FilterContextValue = {
    ...filterContext,
    onClearFilter: () => setSearchParams(new URLSearchParams({ tab })),
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

const ChatContent: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = parseChatTab(searchParams.get("tab"));

  const items: { name: ChatTab; label: string }[] = [
    { name: "messages", label: t("chat.tabs.messages") },
    { name: "bans", label: t("chat.tabs.bans") },
  ];

  const onSelectTab = (name: ChatTab) => {
    // Повторный клик по активной вкладке не должен сбрасывать фильтр.
    if (name === tab) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", name);
    // Фильтр по контенту и поиск есть только у сообщений, страница — у
    // конкретной выборки; остальные параметры URL остаются как были.
    CHAT_MESSAGES_PARAMS.forEach((param) => next.delete(param));
    setSearchParams(next);
  };

  return (
    <ChatFilterScope tab={tab}>
      <Stack spacing={1}>
        <Paper elevation={0} sx={{ px: 2, pt: 2 }}>
          <Typography variant="h5">{t("chat.pageTitle")}</Typography>
          <Tabs value={tab} scrollButtons="auto" variant="scrollable">
            {items.map(({ name, label }) => (
              <Tab
                key={name}
                value={name}
                label={label}
                onClick={() => onSelectTab(name)}
              />
            ))}
          </Tabs>
        </Paper>

        {tab === "messages" ? (
          <Stack spacing={1}>
            <ChatMessagesFilter />
            <ChatMessagesList />
          </Stack>
        ) : (
          <ChatBansList />
        )}
      </Stack>
    </ChatFilterScope>
  );
};
